import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { FaEye, FaTools } from 'react-icons/fa';
import clsx from 'clsx';
import { useUserContext } from '../../../context/UserContext';
import { useInspirationContext } from './InspirationContext';
import { POINTS_COORDINATES } from '../../../routes/routeQuery';
import AdminToolbar from './AdminToolbar';

const Admin = () => {
    const { product, items, setItems } = useInspirationContext();
    const [activeSku, setActiveSku] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [saveStatus, setSaveStatus] = useState(false);
    const [markerEnabled, setMarkerEnabled] = useState(false);
    const [addPoints, { loading, error }] = useMutation(POINTS_COORDINATES);

    useEffect(() => {
        if (markerEnabled) {
            const handleSetPointEvent = (event) => {
                handleSetMarker(event.detail);
            };
            document.addEventListener('setPointEvent', handleSetPointEvent);
            return () => document.removeEventListener('setPointEvent', handleSetPointEvent);
        }
    }, [markerEnabled, items, setItems]); // Dodano items i setItems do zależności

    useEffect(() => {
        if (!loading && saveStatus) {
            setSaveStatus(false);
        }
    }, [loading, saveStatus]);

    const handleSetMarker = ({ x, y }) => {
        if (activeSku) {
            setItems((prevItems) =>
                prevItems.map((item) =>
                    item.product.sku === activeSku
                        ? { ...item, point: { ...item.point, left: x, top: y, disabled: false } }
                        : item
                )
            );
            setSaveStatus(true);
        }
    };

    const removePointBySku = (sku) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.product.sku === sku ? { ...item, point: { ...item.point, disabled: true } } : item
            )
        );
        setSaveStatus(true);
    };

    const savePoints = () => {
        const pointsToSave = items
            .filter((item) => !item.point.disabled)
            .map((item) => item.point);

        addPoints({
            variables: {
                sku: product.sku,
                points_coordinates: JSON.stringify(pointsToSave),
            },
        }).catch((err) => console.error('Error saving points:', err));
    };

    const toolbarSelectItemHandle = (sku) => {
        setActiveSku(sku);
        setMarkerEnabled(false);
    };

    const drawItem = (item) => (
        <div
            key={item.product.sku}
            onClick={() => toolbarSelectItemHandle(item.product.sku)}
            className={clsx('item', {
                disabled_item: item.point.disabled,
                active: item.product.sku === activeSku,
            })}
        >
            <div>{item.product.sku}</div>
            {!item.point.disabled && (
                <FaEye
                    className={item.point.visible ? 'visible' : 'notvisible'}
                    size={16}
                />
            )}
        </div>
    );

    return (
        <div className="admin-toolbar">
            <FaTools size={25} onClick={() => setEditMode((prev) => !prev)} />
            {editMode && (
                <>
                    <div className="admin-toolbar-items">
                        {items.map((item) => drawItem(item))}
                    </div>
                    {activeSku && (
                        <AdminToolbar
                            item={items.find((item) => item.product.sku === activeSku)}
                            removePoint={removePointBySku}
                            markerEnabled={markerEnabled}
                            setMarkerEnabled={() => setMarkerEnabled((prev) => !prev)}
                        />
                    )}
                    {saveStatus && (
                        <div className="save" onClick={savePoints}>
                            Save Points
                        </div>
                    )}
                    {loading && <div>Saving...</div>}
                    {error && <div>Error saving points</div>}
                </>
            )}
        </div>
    );
};

Admin.propTypes = {
    // Brak bezpośrednich propsów, ale kontekst dostarcza product i items
};

export const AdminDecorator = (WrappedComponent) => {
    const DecoratedComponent = (props) => {
        const { user } = useUserContext();

        return user?.is_admin ? (
            <WrappedComponent {...props} adminMode={true}>
                <Admin />
            </WrappedComponent>
        ) : (
            <WrappedComponent {...props} />
        );
    };

    DecoratedComponent.displayName = `AdminDecorator(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return DecoratedComponent;
};

export default memo(Admin);